import {
    Building2,
    FileCheck2,
    FileInput,
    FileOutput,
    Pill,
    Plus,
    ScrollText,
    Stethoscope,
    Trash2,
    UserRound,
} from 'lucide-react';
import {
    useEffect,
    useState,
    type FormEvent,
    type ReactNode,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../Components/data/PageHeader';
import { AsyncSearchSelect, type AsyncSelectOption, type PaginatedResponse } from '../Components/forms/AsyncSearchSelect';
import { Field } from '../Components/forms/Field';
import { FormStickyFooter } from '../Components/forms/FormStickyFooter';
import { Button } from '../Components/ui/Button';
import { Card } from '../Components/ui/Card';
import { Checkbox } from '../Components/ui/Checkbox';
import { Input } from '../Components/ui/Input';
import { Select } from '../Components/ui/Select';
import { Textarea } from '../Components/ui/Textarea';
import NexusLayout from '../Layouts/NexusLayout';
import api from '../services/api';

type DataRecord = Record<string, unknown> & { id?: number | string };
type OptionRecord = DataRecord & { nome?: string; descricao?: string };
type SearchOption = AsyncSelectOption & OptionRecord;

const estados = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const listasControle = ['A1', 'A2', 'A3', 'B1', 'B2', 'C1', 'C2', 'C3', 'C4', 'C5', 'D1', 'D2', 'ANTIMICROBIANO'];
const formasFarmaceuticas = ['Comprimido', 'Cápsula', 'Solução oral', 'Solução injetável', 'Pomada', 'Creme', 'Xarope', 'Suspensão', 'Gotas', 'Outros'];
const unidades = ['UN', 'CAI', 'CP', 'FR', 'ML', 'MG', 'G', 'KG'];
const tiposReceita = ['BRANCA', 'AZUL', 'AMARELA', 'ANTIMICROBIANO', 'ESPECIAL'];

function listData(payload: unknown): OptionRecord[] {
    if (Array.isArray(payload)) return payload as OptionRecord[];
    const data = (payload as { data?: unknown })?.data;
    return Array.isArray(data) ? data as OptionRecord[] : [];
}

function messageFrom(error: unknown) {
    const response = (error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })?.response;
    const errors = response?.data?.errors;
    return (errors ? Object.values(errors).flat()[0] : null) ?? response?.data?.message ?? 'Não foi possível salvar o registro.';
}

function dateValue(value: unknown) {
    return value ? String(value).slice(0, 10) : '';
}

function valueText(value: unknown) {
    return value === null || value === undefined ? '' : String(value);
}

function joinDescription(parts: unknown[]) {
    return parts.map(valueText).filter(Boolean).join(' · ');
}

function optionId(record: OptionRecord) {
    return record.id as number | string;
}

function paginatedOptions(
    payload: unknown,
    mapper: (record: OptionRecord) => SearchOption,
): PaginatedResponse<SearchOption> {
    const meta = payload as { current_page?: number; last_page?: number; total?: number };
    return {
        data: listData(payload).filter((record) => record.id !== undefined && record.id !== null).map(mapper),
        current_page: meta.current_page,
        last_page: meta.last_page,
        total: meta.total,
        has_more: Boolean(meta.current_page && meta.last_page && meta.current_page < meta.last_page),
    };
}

async function loadSearchOptions(
    endpoint: string,
    query: string,
    page: number,
    mapper: (record: OptionRecord) => SearchOption,
    extraParams: Record<string, unknown> = {},
) {
    const { data } = await api.get(endpoint, {
        params: {
            ...extraParams,
            search: query,
            page,
            per_page: 20,
        },
    });

    return paginatedOptions(data, mapper);
}

function productLabel(record?: OptionRecord | null) {
    if (!record) return '';
    const code = valueText(record.codigo);
    const description = valueText(record.descricao ?? record.nome);
    return code ? `${code} - ${description}` : description;
}

function productOption(record: OptionRecord): SearchOption {
    return {
        ...record,
        id: optionId(record),
        label: productLabel(record),
        description: joinDescription([
            record.registro_ms,
            record.lista_controle,
            record.unidade,
        ]),
    };
}

function supplierLabel(record?: OptionRecord | null) {
    return valueText(record?.nome ?? record?.razao_social ?? record?.nome_fantasia);
}

function supplierOption(record: OptionRecord): SearchOption {
    return {
        ...record,
        id: optionId(record),
        label: supplierLabel(record),
        description: joinDescription([record.cnpj, record.telefone, record.email]),
    };
}

function doctorLabel(record?: OptionRecord | null) {
    if (!record) return '';
    const crm = joinDescription([record.crm ? `CRM ${record.crm}` : '', record.uf_crm]);
    return crm ? `${valueText(record.nome)} - ${crm}` : valueText(record.nome);
}

function doctorOption(record: OptionRecord): SearchOption {
    return {
        ...record,
        id: optionId(record),
        label: doctorLabel(record),
        description: joinDescription([record.especialidade, record.cpf, record.telefone]),
    };
}

function patientLabel(record?: OptionRecord | null) {
    if (!record) return '';
    const cpf = valueText(record.cpf);
    return cpf ? `${valueText(record.nome)} - ${cpf}` : valueText(record.nome);
}

function patientOption(record: OptionRecord): SearchOption {
    return {
        ...record,
        id: optionId(record),
        label: patientLabel(record),
        description: joinDescription([record.telefone, record.cidade, record.uf]),
    };
}

function recipeLabel(record?: OptionRecord | null) {
    if (!record) return '';
    return joinDescription([record.numero_receita, record.nome_paciente]) || valueText(record.id);
}

function recipeOption(record: OptionRecord): SearchOption {
    const medico = record.medico as OptionRecord | undefined;
    return {
        ...record,
        id: optionId(record),
        label: recipeLabel(record),
        description: joinDescription([record.tipo_receita, medico?.nome, record.data_emissao ? dateValue(record.data_emissao) : '']),
    };
}

function SearchOptionContent({ option, icon }: { option: SearchOption; icon: ReactNode }) {
    return (
        <div className="nx-flex nx-items-center nx-gap-1.5 nx-min-w-0 nx-w-full">
            <span className="nx-text-muted nx-shrink-0">{icon}</span>
            <div className="nx-min-w-0 nx-flex-1">
                <div className="nx-truncate nx-font-medium nx-text-xs">{option.label}</div>
                {option.description ? <div className="nx-text-[11px] nx-text-muted nx-truncate">{option.description}</div> : null}
            </div>
        </div>
    );
}

function ProductSearch({
    id,
    value,
    initialRecord,
    disabled,
    onChange,
    className,
}: {
    id: string;
    value: string;
    initialRecord?: OptionRecord | null;
    disabled?: boolean;
    onChange: (value: string, option: SearchOption | null) => void;
    className?: string;
}) {
    return (
        <AsyncSearchSelect<SearchOption>
            id={id}
            label="Buscar Produto *"
            value={value}
            initialLabel={productLabel(initialRecord)}
            placeholder="Buscar e selecionar produto"
            searchPlaceholder="Buscar por código, descrição ou registro MS..."
            required
            disabled={disabled}
            clearable={!disabled}
            className={className}
            loadOptions={(query, page) => loadSearchOptions('/produtos', query, page, productOption, { ativo: 1 })}
            onChange={(selectedValue, option) => onChange(valueText(selectedValue), option)}
            renderOption={(option) => <SearchOptionContent option={option} icon={<Pill size={13} />} />}
        />
    );
}

function SupplierSearch({ value, initialRecord, onChange, className }: { value: string; initialRecord?: OptionRecord | null; onChange: (value: string, option: SearchOption | null) => void; className?: string }) {
    return (
        <AsyncSearchSelect<SearchOption>
            id="buscar_fornecedor"
            label="Buscar Fornecedor"
            value={value}
            initialLabel={supplierLabel(initialRecord)}
            placeholder="Buscar e selecionar fornecedor"
            searchPlaceholder="Buscar por nome ou CNPJ..."
            className={className}
            loadOptions={(query, page) => loadSearchOptions('/fornecedores', query, page, supplierOption)}
            onChange={(selectedValue, option) => onChange(valueText(selectedValue), option)}
            renderOption={(option) => <SearchOptionContent option={option} icon={<Building2 size={13} />} />}
        />
    );
}

function DoctorSearch({ value, initialRecord, onChange, className }: { value: string; initialRecord?: OptionRecord | null; onChange: (value: string, option: SearchOption | null) => void; className?: string }) {
    return (
        <AsyncSearchSelect<SearchOption>
            id="buscar_medico"
            label="Buscar Médico *"
            value={value}
            initialLabel={doctorLabel(initialRecord)}
            placeholder="Buscar e selecionar médico"
            searchPlaceholder="Buscar por nome, CRM ou especialidade..."
            required
            className={className}
            loadOptions={(query, page) => loadSearchOptions('/medicos', query, page, doctorOption, { ativo: 1 })}
            onChange={(selectedValue, option) => onChange(valueText(selectedValue), option)}
            renderOption={(option) => <SearchOptionContent option={option} icon={<Stethoscope size={13} />} />}
        />
    );
}

function PatientSearch({ value, initialRecord, onChange, className }: { value: string; initialRecord?: OptionRecord | null; onChange: (value: string, option: SearchOption | null) => void; className?: string }) {
    return (
        <AsyncSearchSelect<SearchOption>
            id="buscar_paciente"
            label="Buscar Paciente *"
            value={value}
            initialLabel={patientLabel(initialRecord)}
            placeholder="Buscar e selecionar paciente"
            searchPlaceholder="Buscar por nome, CPF ou telefone..."
            required
            className={className}
            loadOptions={(query, page) => loadSearchOptions('/pacientes', query, page, patientOption, { ativo: 1 })}
            onChange={(selectedValue, option) => onChange(valueText(selectedValue), option)}
            renderOption={(option) => <SearchOptionContent option={option} icon={<UserRound size={13} />} />}
        />
    );
}

function RecipeSearch({ value, initialRecord, onChange, className }: { value: string; initialRecord?: OptionRecord | null; onChange: (value: string, option: SearchOption | null) => void; className?: string }) {
    return (
        <AsyncSearchSelect<SearchOption>
            id="buscar_receita"
            label="Buscar Receita"
            value={value}
            initialLabel={recipeLabel(initialRecord)}
            placeholder="Receita avulsa / não informada"
            searchPlaceholder="Buscar por número, paciente, CPF ou médico..."
            className={className}
            loadOptions={(query, page) => loadSearchOptions('/receitas', query, page, recipeOption)}
            onChange={(selectedValue, option) => onChange(valueText(selectedValue), option)}
            renderOption={(option) => <SearchOptionContent option={option} icon={<ScrollText size={13} />} />}
        />
    );
}

function FormPage({
    title,
    description,
    icon,
    listPath,
    saving,
    error,
    children,
    onSubmit,
}: {
    title: string;
    description: string;
    icon: ReactNode;
    listPath: string;
    saving: boolean;
    error: string | null;
    children: ReactNode;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
    const navigate = useNavigate();
    return (
        <NexusLayout title={title}>
            <form className="nx-page nx-sngpc-form-page" onSubmit={onSubmit}>
                <PageHeader
                    icon={icon}
                    title={title}
                    description={description}
                    onBack={() => navigate(listPath)}
                    backLabel="Voltar"
                />
                {error ? <div className="nx-sngpc-error" role="alert">{error}</div> : null}
                {children}
                <FormStickyFooter
                    loading={saving}
                    saveLabel="Salvar"
                    backLabel="Cancelar"
                    onBack={() => navigate(listPath)}
                />
            </form>
        </NexusLayout>
    );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="nx-sngpc-form-section">
            <h2>{title}</h2>
            <div className="nx-form-grid nx-sngpc-page-form-grid">{children}</div>
        </section>
    );
}

export function ProdutoForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState<Record<string, unknown>>({
        codigo: '', codigo_barras: '', descricao: '', principio_ativo: '', concentracao: '',
        forma_farmaceutica: '', tipo_produto: 'MEDICAMENTO', lista_controle: '', registro_ms: '',
        fabricante: '', unidade: 'UN', preco_custo: 0, preco_venda: 0, estoque_minimo: 0,
        controla_lote: true, controla_validade: true, ativo: true, observacoes: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        api.get(`/produtos/${id}`).then(({ data }) => setForm(data)).catch(() => setError('Não foi possível carregar o produto.'));
    }, [id]);

    const set = (name: string, value: unknown) => setForm((current) => ({ ...current, [name]: value }));
    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault(); setSaving(true); setError(null);
        try {
            if (id) await api.put(`/produtos/${id}`, form); else await api.post('/produtos', form);
            navigate('/produtos');
        } catch (requestError) { setError(messageFrom(requestError)); } finally { setSaving(false); }
    }

    return (
        <FormPage title={id ? 'Editar produto controlado' : 'Novo produto controlado'} description="Dados cadastrais e classificação do medicamento sujeito a controle especial." icon={<Pill size={21} />} listPath="/produtos" saving={saving} error={error} onSubmit={submit}>
            <Card className="nx-page-card nx-sngpc-form-card">
                <Section title="Identificação">
                    <Field label="Código" htmlFor="codigo"><Input readOnly value={id ? String(form.codigo ?? '') : 'Gerado ao salvar'} /></Field>
                    <Field label="Código de barras" htmlFor="codigo_barras"><Input value={String(form.codigo_barras ?? '')} onChange={(e) => set('codigo_barras', e.target.value)} /></Field>
                    <Field label="Descrição" htmlFor="descricao" className="nx-sngpc-field--wide"><Input required value={String(form.descricao ?? '')} onChange={(e) => set('descricao', e.target.value)} /></Field>
                    <Field label="DCB / princípio ativo" htmlFor="principio_ativo" className="nx-sngpc-field--wide"><Input value={String(form.principio_ativo ?? '')} onChange={(e) => set('principio_ativo', e.target.value)} /></Field>
                    <Field label="Concentração" htmlFor="concentracao"><Input value={String(form.concentracao ?? '')} onChange={(e) => set('concentracao', e.target.value)} /></Field>
                    <Field label="Fabricante" htmlFor="fabricante"><Input value={String(form.fabricante ?? '')} onChange={(e) => set('fabricante', e.target.value)} /></Field>
                </Section>
                <Section title="Classificação e controle">
                    <Field label="Número de registro MS" htmlFor="registro_ms"><Input value={String(form.registro_ms ?? '')} onChange={(e) => set('registro_ms', e.target.value)} /></Field>
                    <Field label="Lista de controle" htmlFor="lista_controle"><Select value={String(form.lista_controle ?? '')} onChange={(e) => set('lista_controle', e.target.value)}><option value="">Selecione</option>{listasControle.map((item) => <option key={item}>{item}</option>)}</Select></Field>
                    <Field label="Forma farmacêutica" htmlFor="forma_farmaceutica"><Select value={String(form.forma_farmaceutica ?? '')} onChange={(e) => set('forma_farmaceutica', e.target.value)}><option value="">Selecione</option>{formasFarmaceuticas.map((item) => <option key={item}>{item}</option>)}</Select></Field>
                    <Field label="Unidade de medida" htmlFor="unidade"><Select required value={String(form.unidade ?? 'UN')} onChange={(e) => set('unidade', e.target.value)}>{unidades.map((item) => <option key={item}>{item}</option>)}</Select></Field>
                    <Field label="Estoque mínimo" htmlFor="estoque_minimo"><Input type="number" min="0" step="0.01" value={String(form.estoque_minimo ?? 0)} onChange={(e) => set('estoque_minimo', Number(e.target.value))} /></Field>
                    <Field label="Preço de custo" htmlFor="preco_custo"><Input type="number" min="0" step="0.01" value={String(form.preco_custo ?? 0)} onChange={(e) => set('preco_custo', Number(e.target.value))} /></Field>
                    <Field label="Preço de venda" htmlFor="preco_venda"><Input type="number" min="0" step="0.01" value={String(form.preco_venda ?? 0)} onChange={(e) => set('preco_venda', Number(e.target.value))} /></Field>
                    <div className="nx-sngpc-checks nx-sngpc-field--wide">
                        {([['controla_lote', 'Controla lote'], ['controla_validade', 'Controla validade'], ['ativo', 'Cadastro ativo']] as const).map(([name, label]) => <label key={name}><Checkbox checked={Boolean(form[name])} onChange={(e) => set(name, e.target.checked)} />{label}</label>)}
                    </div>
                    <Field label="Observações" htmlFor="observacoes" className="nx-sngpc-field--full"><Textarea rows={4} value={String(form.observacoes ?? '')} onChange={(e) => set('observacoes', e.target.value)} /></Field>
                </Section>
            </Card>
        </FormPage>
    );
}

export function EntradaForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState<Record<string, unknown>>({ produto_id: '', fornecedor_id: '', tipo_entrada: 'COMPRA', data_entrada: new Date().toISOString().slice(0, 10), numero_nota: '', serie_nota: '', chave_nfe: '', cnpj_fornecedor: '', nome_fornecedor: '', lote: '', data_fabricacao: '', data_validade: '', quantidade: 1, valor_unitario: 0, valor_total: 0, numero_registro_anvisa: '', observacoes: '' });
    const [saving, setSaving] = useState(false); const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        if (id) api.get(`/entradas/${id}`).then(({ data }) => setForm({ ...data, data_entrada: dateValue(data.data_entrada), data_fabricacao: dateValue(data.data_fabricacao), data_validade: dateValue(data.data_validade) })).catch(() => setError('Não foi possível carregar a entrada.'));
    }, [id]);
    const set = (name: string, value: unknown) => setForm((current) => ({ ...current, [name]: value }));
    const supplierChanged = (value: string, supplier: SearchOption | null) => setForm((current) => ({ ...current, fornecedor_id: value || null, nome_fornecedor: supplier?.nome ?? '', cnpj_fornecedor: supplier?.cnpj ?? '' }));
    async function submit(event: FormEvent<HTMLFormElement>) { event.preventDefault(); setSaving(true); setError(null); try { if (id) await api.put(`/entradas/${id}`, form); else await api.post('/entradas', form); navigate('/entradas'); } catch (e) { setError(messageFrom(e)); } finally { setSaving(false); } }
    return (
        <FormPage title={id ? 'Editar entrada' : 'Nova entrada'} description="Registro de aquisição ou movimentação de entrada no estoque controlado." icon={<FileInput size={21} />} listPath="/entradas" saving={saving} error={error} onSubmit={submit}>
            <Card className="nx-page-card nx-sngpc-form-card">
                <Section title="Produto e documento">
                    <ProductSearch id="entrada_produto" className="nx-sngpc-field--wide" value={String(form.produto_id ?? '')} initialRecord={form.produto as OptionRecord | undefined} disabled={Boolean(id)} onChange={(value) => set('produto_id', value)} />
                    <Field label="Tipo de entrada" htmlFor="tipo_entrada"><Select required value={String(form.tipo_entrada ?? 'COMPRA')} disabled={Boolean(id)} onChange={(e) => set('tipo_entrada', e.target.value)}>{['COMPRA', 'TRANSFERENCIA', 'DEVOLUCAO'].map((v) => <option key={v}>{v}</option>)}</Select></Field>
                    <Field label="Número da nota fiscal" htmlFor="numero_nota"><Input required value={String(form.numero_nota ?? '')} onChange={(e) => set('numero_nota', e.target.value)} /></Field>
                    <Field label="Série" htmlFor="serie_nota"><Input value={String(form.serie_nota ?? '')} onChange={(e) => set('serie_nota', e.target.value)} /></Field>
                    <Field label="Data da nota / entrada" htmlFor="data_entrada"><Input type="date" required value={String(form.data_entrada ?? '')} onChange={(e) => set('data_entrada', e.target.value)} /></Field>
                    <Field label="Chave da NF-e" htmlFor="chave_nfe" className="nx-sngpc-field--wide"><Input maxLength={44} value={String(form.chave_nfe ?? '')} onChange={(e) => set('chave_nfe', e.target.value)} /></Field>
                </Section>
                <Section title="Fornecedor">
                    <SupplierSearch className="nx-sngpc-field--wide" value={String(form.fornecedor_id ?? '')} initialRecord={form.fornecedor as OptionRecord | undefined} onChange={supplierChanged} />
                    <Field label="CNPJ do fornecedor" htmlFor="cnpj_fornecedor"><Input value={String(form.cnpj_fornecedor ?? '')} onChange={(e) => set('cnpj_fornecedor', e.target.value)} /></Field>
                    <Field label="Nome do fornecedor" htmlFor="nome_fornecedor" className="nx-sngpc-field--wide"><Input value={String(form.nome_fornecedor ?? '')} onChange={(e) => set('nome_fornecedor', e.target.value)} /></Field>
                </Section>
                <Section title="Lote e quantidade">
                    <Field label="Lote" htmlFor="lote"><Input required disabled={Boolean(id)} value={String(form.lote ?? '')} onChange={(e) => set('lote', e.target.value)} /></Field>
                    <Field label="Data de fabricação" htmlFor="data_fabricacao"><Input type="date" disabled={Boolean(id)} value={String(form.data_fabricacao ?? '')} onChange={(e) => set('data_fabricacao', e.target.value)} /></Field>
                    <Field label="Data de validade" htmlFor="data_validade"><Input type="date" disabled={Boolean(id)} value={String(form.data_validade ?? '')} onChange={(e) => set('data_validade', e.target.value)} /></Field>
                    <Field label="Quantidade" htmlFor="quantidade"><Input type="number" min="0.01" step="0.01" required disabled={Boolean(id)} value={String(form.quantidade ?? 1)} onChange={(e) => set('quantidade', Number(e.target.value))} /></Field>
                    <Field label="Valor unitário" htmlFor="valor_unitario"><Input type="number" min="0" step="0.01" disabled={Boolean(id)} value={String(form.valor_unitario ?? 0)} onChange={(e) => set('valor_unitario', Number(e.target.value))} /></Field>
                    <Field label="Registro ANVISA" htmlFor="numero_registro_anvisa"><Input value={String(form.numero_registro_anvisa ?? '')} onChange={(e) => set('numero_registro_anvisa', e.target.value)} /></Field>
                    <Field label="Observações" htmlFor="entrada_observacoes" className="nx-sngpc-field--full"><Textarea rows={4} value={String(form.observacoes ?? '')} onChange={(e) => set('observacoes', e.target.value)} /></Field>
                </Section>
            </Card>
        </FormPage>
    );
}

type RecipeItem = { produto_id: string; produto?: OptionRecord | null; quantidade: number; quantidade_dispensada: number; posologia: string; duracao_dias: string };
const blankRecipeItem = (): RecipeItem => ({ produto_id: '', quantidade: 1, quantidade_dispensada: 0, posologia: '', duracao_dias: '' });

export function ReceitaForm() {
    const { id } = useParams(); const navigate = useNavigate();
    const [form, setForm] = useState<Record<string, unknown>>({ medico_id: '', paciente_id: '', numero_receita: '', data_emissao: new Date().toISOString().slice(0, 10), data_atendimento: '', tipo_receita: 'BRANCA', cid: '', uf_atendimento: '', data_validade_receita: '', status: 'ABERTA', retida: true, observacoes: '' });
    const [items, setItems] = useState<RecipeItem[]>([blankRecipeItem()]); const [saving, setSaving] = useState(false); const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        if (id) api.get(`/receitas/${id}`).then(({ data }) => { setForm({ ...data, data_emissao: dateValue(data.data_emissao), data_atendimento: dateValue(data.data_atendimento), data_validade_receita: dateValue(data.data_validade_receita) }); setItems((data.produtos ?? []).map((product: DataRecord) => ({ produto_id: String(product.id), produto: product, quantidade: Number((product.pivot as DataRecord)?.quantidade ?? 1), quantidade_dispensada: Number((product.pivot as DataRecord)?.quantidade_dispensada ?? 0), posologia: String((product.pivot as DataRecord)?.posologia ?? ''), duracao_dias: String((product.pivot as DataRecord)?.duracao_dias ?? '') }))); }).catch(() => setError('Não foi possível carregar a receita.'));
    }, [id]);
    const set = (name: string, value: unknown) => setForm((current) => ({ ...current, [name]: value }));
    const setItem = (index: number, name: keyof RecipeItem, value: string | number) => setItems((current) => current.map((item, i) => i === index ? { ...item, [name]: value } : item));
    const setRecipeProduct = (index: number, value: string, product: SearchOption | null) => setItems((current) => current.map((item, i) => i === index ? { ...item, produto_id: value, produto: product } : item));
    async function submit(event: FormEvent<HTMLFormElement>) { event.preventDefault(); setSaving(true); setError(null); try { const payload = { ...form, itens: items.map((item) => ({ produto_id: item.produto_id, quantidade: item.quantidade, quantidade_dispensada: item.quantidade_dispensada, posologia: item.posologia, duracao_dias: item.duracao_dias })) }; if (id) await api.put(`/receitas/${id}`, payload); else await api.post('/receitas', payload); navigate('/receitas'); } catch (e) { setError(messageFrom(e)); } finally { setSaving(false); } }
    return (
        <FormPage title={id ? 'Editar receita' : 'Nova receita'} description="Prescrição, paciente e medicamentos controlados." icon={<FileCheck2 size={21} />} listPath="/receitas" saving={saving} error={error} onSubmit={submit}>
            <Card className="nx-page-card nx-sngpc-form-card">
                <Section title="Prescrição">
                    <Field label="Número da receita" htmlFor="numero_receita"><Input required value={String(form.numero_receita ?? '')} onChange={(e) => set('numero_receita', e.target.value)} /></Field>
                    <Field label="Tipo de receita" htmlFor="tipo_receita"><Select required value={String(form.tipo_receita ?? '')} onChange={(e) => set('tipo_receita', e.target.value)}>{tiposReceita.map((v) => <option key={v}>{v}</option>)}</Select></Field>
                    <Field label="Data da receita" htmlFor="data_emissao"><Input type="date" required value={String(form.data_emissao ?? '')} onChange={(e) => set('data_emissao', e.target.value)} /></Field>
                    <Field label="Data do atendimento" htmlFor="data_atendimento"><Input type="date" value={String(form.data_atendimento ?? '')} onChange={(e) => set('data_atendimento', e.target.value)} /></Field>
                    <Field label="Validade da receita" htmlFor="data_validade_receita"><Input type="date" value={String(form.data_validade_receita ?? '')} onChange={(e) => set('data_validade_receita', e.target.value)} /></Field>
                    <Field label="Situação" htmlFor="receita_status"><Select value={String(form.status ?? 'ABERTA')} onChange={(e) => set('status', e.target.value)}>{['ABERTA', 'DISPENSADA', 'CANCELADA', 'VENCIDA'].map((v) => <option key={v}>{v}</option>)}</Select></Field>
                </Section>
                <Section title="Prescritor e paciente">
                    <DoctorSearch className="nx-sngpc-field--wide" value={String(form.medico_id ?? '')} initialRecord={form.medico as OptionRecord | undefined} onChange={(value) => set('medico_id', value)} />
                    <PatientSearch className="nx-sngpc-field--wide" value={String(form.paciente_id ?? '')} initialRecord={form.paciente as OptionRecord | undefined} onChange={(value) => set('paciente_id', value)} />
                    <Field label="CID" htmlFor="receita_cid"><Input value={String(form.cid ?? '')} onChange={(e) => set('cid', e.target.value)} /></Field>
                    <Field label="UF do atendimento" htmlFor="uf_atendimento"><Select value={String(form.uf_atendimento ?? '')} onChange={(e) => set('uf_atendimento', e.target.value)}><option value="">Selecione</option>{estados.map((v) => <option key={v}>{v}</option>)}</Select></Field>
                    <label className="nx-sngpc-checkbox-field nx-sngpc-field--wide"><Checkbox checked={Boolean(form.retida)} onChange={(e) => set('retida', e.target.checked)} /><span>Receita retida</span></label>
                </Section>
                <section className="nx-sngpc-form-section">
                    <div className="nx-sngpc-section-head"><h2>Medicamentos prescritos</h2><Button type="button" variant="ghost" onClick={() => setItems((current) => [...current, blankRecipeItem()])}><Plus size={16} /> Adicionar item</Button></div>
                    <div className="nx-sngpc-items">
                        {items.map((item, index) => <div className="nx-sngpc-item-row" key={index}>
                            <ProductSearch id={`item_produto_${index}`} className="nx-sngpc-item-product" value={item.produto_id} initialRecord={item.produto} onChange={(value, product) => setRecipeProduct(index, value, product)} />
                            <Field label="Quantidade prescrita" htmlFor={`item_quantidade_${index}`}><Input type="number" min="0.01" step="0.01" required value={item.quantidade} onChange={(e) => setItem(index, 'quantidade', Number(e.target.value))} /></Field>
                            <Field label="Quantidade dispensada" htmlFor={`item_dispensada_${index}`}><Input type="number" min="0" step="0.01" value={item.quantidade_dispensada} onChange={(e) => setItem(index, 'quantidade_dispensada', Number(e.target.value))} /></Field>
                            <Field label="Posologia" htmlFor={`item_posologia_${index}`}><Input value={item.posologia} onChange={(e) => setItem(index, 'posologia', e.target.value)} /></Field>
                            <Button type="button" variant="ghost" className="nx-sngpc-remove-item" aria-label="Remover item" disabled={items.length === 1} onClick={() => setItems((current) => current.filter((_, i) => i !== index))}><Trash2 size={16} /></Button>
                        </div>)}
                    </div>
                    <Field label="Observações" htmlFor="receita_observacoes"><Textarea rows={4} value={String(form.observacoes ?? '')} onChange={(e) => set('observacoes', e.target.value)} /></Field>
                </section>
            </Card>
        </FormPage>
    );
}

export function SaidaForm() {
    const { id } = useParams(); const navigate = useNavigate();
    const [lots, setLots] = useState<OptionRecord[]>([]);
    const [form, setForm] = useState<Record<string, unknown>>({ produto_id: '', receita_id: '', data_saida: new Date().toISOString().slice(0, 10), data_dispensacao: '', tipo_saida: 'DISPENSACAO', lote: '', data_validade_lote: '', quantidade: 1, nome_paciente: '', cpf_paciente: '', nome_medico: '', crm_medico: '', farmaceutico_nome: '', farmaceutico_crf: '', numero_registro_sngpc: '', tipo_receita_avulso: '', numero_receita_avulso: '', data_receita_avulso: '', prescritor_nome: '', prescritor_conselho_tipo: 'CRM', prescritor_conselho_numero: '', prescritor_conselho_uf: '', comprador_nome: '', comprador_doc_tipo: 'CPF', comprador_doc_numero: '', comprador_doc_orgao: '', comprador_doc_uf: '', comprador_sexo: '', comprador_idade: '', cid: '', observacoes: '' });
    const [saving, setSaving] = useState(false); const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        if (id) api.get(`/saidas/${id}`).then(({ data }) => setForm({ ...data, data_saida: dateValue(data.data_saida), data_dispensacao: data.data_dispensacao ? String(data.data_dispensacao).slice(0, 16) : '', data_validade_lote: dateValue(data.data_validade_lote), data_receita_avulso: dateValue(data.data_receita_avulso) })).catch(() => setError('Não foi possível carregar a saída.'));
    }, [id]);
    const set = (name: string, value: unknown) => setForm((current) => ({ ...current, [name]: value }));
    async function productChanged(value: string) { set('produto_id', value); if (!value) return setLots([]); const { data } = await api.get(`/produtos/${value}/saldo`); setLots(data.lotes ?? []); }
    function recipeChanged(value: string, recipe: SearchOption | null) { const doctor = recipe?.medico as DataRecord | undefined; setForm((current) => ({ ...current, receita_id: value || null, receita: recipe, nome_paciente: recipe?.nome_paciente ?? current.nome_paciente, cpf_paciente: recipe?.cpf_paciente ?? current.cpf_paciente, nome_medico: doctor?.nome ?? current.nome_medico, crm_medico: doctor?.crm ?? current.crm_medico, prescritor_nome: doctor?.nome ?? current.prescritor_nome, prescritor_conselho_numero: doctor?.crm ?? current.prescritor_conselho_numero, prescritor_conselho_uf: doctor?.uf_crm ?? current.prescritor_conselho_uf, numero_receita_avulso: recipe?.numero_receita ?? current.numero_receita_avulso, tipo_receita_avulso: recipe?.tipo_receita ?? current.tipo_receita_avulso })); }
    async function submit(event: FormEvent<HTMLFormElement>) { event.preventDefault(); setSaving(true); setError(null); try { if (id) await api.put(`/saidas/${id}`, form); else await api.post('/saidas', form); navigate('/saidas'); } catch (e) { setError(messageFrom(e)); } finally { setSaving(false); } }
    return (
        <FormPage title={id ? 'Editar saída' : 'Nova saída'} description="Dispensação e demais baixas de produtos sujeitos a controle especial." icon={<FileOutput size={21} />} listPath="/saidas" saving={saving} error={error} onSubmit={submit}>
            <Card className="nx-page-card nx-sngpc-form-card">
                <Section title="Movimentação">
                    <ProductSearch id="saida_produto" className="nx-sngpc-field--wide" value={String(form.produto_id ?? '')} initialRecord={form.produto as OptionRecord | undefined} disabled={Boolean(id)} onChange={(value) => productChanged(value)} />
                    <Field label="Motivo da saída" htmlFor="tipo_saida"><Select required disabled={Boolean(id)} value={String(form.tipo_saida ?? '')} onChange={(e) => set('tipo_saida', e.target.value)}>{['DISPENSACAO', 'PERDA', 'USO_INTERNO', 'VENDA', 'TRANSFERENCIA'].map((v) => <option key={v}>{v}</option>)}</Select></Field>
                    <Field label="Data da saída" htmlFor="data_saida"><Input type="date" required disabled={Boolean(id)} value={String(form.data_saida ?? '')} onChange={(e) => set('data_saida', e.target.value)} /></Field>
                    <Field label="Data e hora da dispensação" htmlFor="data_dispensacao"><Input type="datetime-local" value={String(form.data_dispensacao ?? '')} onChange={(e) => set('data_dispensacao', e.target.value)} /></Field>
                    <Field label="Lote" htmlFor="saida_lote"><Select required disabled={Boolean(id)} value={String(form.lote ?? '')} onChange={(e) => { const lot = lots.find((item) => String(item.lote) === e.target.value); setForm((current) => ({ ...current, lote: e.target.value, data_validade_lote: lot?.data_validade ?? current.data_validade_lote })); }}><option value="">Selecione</option>{form.lote && !lots.some((item) => item.lote === form.lote) ? <option value={String(form.lote)}>{String(form.lote)}</option> : null}{lots.map((item) => <option key={String(item.lote)} value={String(item.lote)}>{String(item.lote)} - saldo {String(item.quantidade)}</option>)}</Select></Field>
                    <Field label="Validade do lote" htmlFor="data_validade_lote"><Input type="date" disabled={Boolean(id)} value={dateValue(form.data_validade_lote)} onChange={(e) => set('data_validade_lote', e.target.value)} /></Field>
                    <Field label="Quantidade" htmlFor="saida_quantidade"><Input type="number" min="0.01" step="0.01" required disabled={Boolean(id)} value={String(form.quantidade ?? 1)} onChange={(e) => set('quantidade', Number(e.target.value))} /></Field>
                </Section>
                <Section title="Receita e prescritor">
                    <RecipeSearch className="nx-sngpc-field--wide" value={String(form.receita_id ?? '')} initialRecord={form.receita as OptionRecord | undefined} onChange={recipeChanged} />
                    <Field label="Tipo da receita" htmlFor="tipo_receita_avulso"><Select value={String(form.tipo_receita_avulso ?? '')} onChange={(e) => set('tipo_receita_avulso', e.target.value)}><option value="">Selecione</option>{tiposReceita.map((v) => <option key={v}>{v}</option>)}</Select></Field>
                    <Field label="Número da receita" htmlFor="numero_receita_avulso"><Input value={String(form.numero_receita_avulso ?? '')} onChange={(e) => set('numero_receita_avulso', e.target.value)} /></Field>
                    <Field label="Data da receita" htmlFor="data_receita_avulso"><Input type="date" value={String(form.data_receita_avulso ?? '')} onChange={(e) => set('data_receita_avulso', e.target.value)} /></Field>
                    <Field label="Nome do prescritor" htmlFor="prescritor_nome" className="nx-sngpc-field--wide"><Input value={String(form.prescritor_nome ?? '')} onChange={(e) => set('prescritor_nome', e.target.value)} /></Field>
                    <Field label="Conselho" htmlFor="prescritor_conselho_tipo"><Select value={String(form.prescritor_conselho_tipo ?? 'CRM')} onChange={(e) => set('prescritor_conselho_tipo', e.target.value)}>{['CRM', 'CRO', 'CRMV'].map((v) => <option key={v}>{v}</option>)}</Select></Field>
                    <Field label="Número do conselho" htmlFor="prescritor_conselho_numero"><Input value={String(form.prescritor_conselho_numero ?? '')} onChange={(e) => set('prescritor_conselho_numero', e.target.value)} /></Field>
                    <Field label="UF do conselho" htmlFor="prescritor_conselho_uf"><Select value={String(form.prescritor_conselho_uf ?? '')} onChange={(e) => set('prescritor_conselho_uf', e.target.value)}><option value="">Selecione</option>{estados.map((v) => <option key={v}>{v}</option>)}</Select></Field>
                    <Field label="CID" htmlFor="saida_cid"><Input value={String(form.cid ?? '')} onChange={(e) => set('cid', e.target.value)} /></Field>
                </Section>
                <Section title="Comprador">
                    <Field label="Nome" htmlFor="comprador_nome" className="nx-sngpc-field--wide"><Input value={String(form.comprador_nome ?? form.nome_paciente ?? '')} onChange={(e) => { set('comprador_nome', e.target.value); set('nome_paciente', e.target.value); }} /></Field>
                    <Field label="Tipo de documento" htmlFor="comprador_doc_tipo"><Select value={String(form.comprador_doc_tipo ?? 'CPF')} onChange={(e) => set('comprador_doc_tipo', e.target.value)}>{['CPF', 'RG', 'CNH', 'PASSAPORTE'].map((v) => <option key={v}>{v}</option>)}</Select></Field>
                    <Field label="Número do documento" htmlFor="comprador_doc_numero"><Input value={String(form.comprador_doc_numero ?? form.cpf_paciente ?? '')} onChange={(e) => { set('comprador_doc_numero', e.target.value); set('cpf_paciente', e.target.value); }} /></Field>
                    <Field label="Órgão emissor" htmlFor="comprador_doc_orgao"><Input value={String(form.comprador_doc_orgao ?? '')} onChange={(e) => set('comprador_doc_orgao', e.target.value)} /></Field>
                    <Field label="UF do documento" htmlFor="comprador_doc_uf"><Select value={String(form.comprador_doc_uf ?? '')} onChange={(e) => set('comprador_doc_uf', e.target.value)}><option value="">Selecione</option>{estados.map((v) => <option key={v}>{v}</option>)}</Select></Field>
                    <Field label="Sexo" htmlFor="comprador_sexo"><Select value={String(form.comprador_sexo ?? '')} onChange={(e) => set('comprador_sexo', e.target.value)}><option value="">Selecione</option><option value="F">Feminino</option><option value="M">Masculino</option><option value="O">Outro</option></Select></Field>
                    <Field label="Idade" htmlFor="comprador_idade"><Input type="number" min="0" max="130" value={String(form.comprador_idade ?? '')} onChange={(e) => set('comprador_idade', e.target.value ? Number(e.target.value) : null)} /></Field>
                </Section>
                <Section title="Responsável técnico">
                    <Field label="Farmacêutico" htmlFor="farmaceutico_nome" className="nx-sngpc-field--wide"><Input value={String(form.farmaceutico_nome ?? '')} onChange={(e) => set('farmaceutico_nome', e.target.value)} /></Field>
                    <Field label="CRF" htmlFor="farmaceutico_crf"><Input value={String(form.farmaceutico_crf ?? '')} onChange={(e) => set('farmaceutico_crf', e.target.value)} /></Field>
                    <Field label="Registro SNGPC" htmlFor="numero_registro_sngpc"><Input value={String(form.numero_registro_sngpc ?? '')} onChange={(e) => set('numero_registro_sngpc', e.target.value)} /></Field>
                    <Field label="Observações" htmlFor="saida_observacoes" className="nx-sngpc-field--full"><Textarea rows={4} value={String(form.observacoes ?? '')} onChange={(e) => set('observacoes', e.target.value)} /></Field>
                </Section>
            </Card>
        </FormPage>
    );
}
